// src/components/CartDropdown.js - COMPLETE FILE
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { X, ShoppingCart, Plus, Minus, Trash2, Search, Phone, User } from 'lucide-react';
import { TransactionService } from '../services/TransactionService';
import { GuestAuthService } from '../services/GuestAuthService'; // ✅ ADD THIS IMPORT
import { auth } from '../firebase/config';
import { useActiveShop } from '../contexts/ActiveShopContext';
import { useNavigate } from 'react-router-dom';
import { /* signInWithPopup, */ signInAnonymously } from 'firebase/auth';
import { safeSignInWithPopup } from '../firebase/config';

import { googleProvider } from '../firebase/config';

const DropdownContainer = styled.div`
  position: fixed;
  top: 80px;
  right: 1rem;
  width: 400px;
  max-height: 600px;
  background: ${props => props.theme?.colors?.background || '#111'};
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: calc(100% - 2rem);
    top: 70px;
    max-height: calc(100vh - 150px);
  }
`;

const LoadingSpinner = styled.div`
  width: ${props => props.size || 20}px;
  height: ${props => props.size || 20}px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const CartHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent}30`};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => `${props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'}50`};
  
  h3 {
    margin: 0;
    font-size: 1.1rem;
    color: ${props => props.theme?.colors?.accent || '#800000'};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .close-btn {
    background: transparent;
    border: none;
    color: ${props => props.theme?.colors?.text || 'white'};
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const CartContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme?.colors?.accent || '#800000'};
    border-radius: 3px;
  }
`;

const CheckoutOptionsSection = styled.div`
  padding: 1rem;
  border-top: 1px solid ${props => `${props.theme?.colors?.accent}30`};
  background: rgba(0, 0, 0, 0.2);
`;

const CheckoutButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: ${props => props.showForm ? '1rem' : '0'};
`;

const CheckoutOptionButton = styled.button`
  padding: 0.875rem;
  background: ${props => props.active 
    ? props.theme?.colors?.accent || '#800000'
    : 'transparent'};
  border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 5px;
  color: ${props => props.active 
    ? 'white' 
    : props.theme?.colors?.accent || '#800000'};
  font-weight: 300;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s;
  
  &:hover {
    background: ${props => props.active
      ? props.theme?.colors?.primary || '#600000'
      : `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)'};
  }
`;

const GuestFormContainer = styled.div`
  max-height: ${props => props.isOpen ? '300px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  
  .form-content {
    padding-top: ${props => props.isOpen ? '0.5rem' : '0'};
  }
`;

const CartSummary = styled.div`
  padding: 1rem;
  border-top: 1px solid ${props => `${props.theme?.colors?.accent}30`};
  background: rgba(0, 0, 0, 0.3);
  
  .summary-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    
    &.total {
      font-size: 1.1rem;
      font-weight: 700;
      color: ${props => props.theme?.colors?.accent || '#800000'};
      border-top: 1px solid ${props => `${props.theme?.colors?.accent}30`};
      padding-top: 0.5rem;
      margin-top: 0.5rem;
    }
  }
`;

const SendButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${props => props.theme?.colors?.accent || '#800000'};
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme?.colors?.primary || '#600000'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: rgba(255, 255, 255, 0.6);
  
  svg {
    width: 64px;
    height: 64px;
    opacity: 0.3;
    margin-bottom: 1rem;
  }
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const GuestInfoSection = styled.div`
  .input-group {
    position: relative;
    margin-bottom: 0.75rem;
    
    svg {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%);
      color: ${props => props.theme?.colors?.accent || '#800000'};
      opacity: 0.6;
      pointer-events: none;
    }
    
    input {
      width: 100%;
      padding: 0.75rem 1rem 0.75rem 3rem;
      background: rgba(0, 0, 0, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      font-size: 0.9rem;
      
      &:focus {
        outline: none;
        border-color: ${props => props.theme?.colors?.accent || '#800000'};
      }
      
      &::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }
    }
  }
  
  .hint {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 0.25rem;
    font-style: italic;
  }
`;

const GuestLookupButton = styled.button`
  width: calc(100% - 2rem);
  margin: 0 1rem;
  padding: 0.875rem;
  background: transparent;
  border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 8px;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s;
  
  &:hover {
    background: ${props => `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)'};
  }
`;

const GuestLookupSection = styled.div`
  padding: 1rem;
  margin: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30`};
  
  .lookup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    
    h4 {
      margin: 0;
      font-size: 0.9rem;
      color: ${props => props.theme?.colors?.accent || '#800000'};
    }
    
    button {
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      padding: 0.25rem;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        color: white;
      }
    }
  }
  
  .input-group {
    position: relative;
    margin-bottom: 0.75rem;
    
    svg {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%);
      color: ${props => props.theme?.colors?.accent || '#800000'};
      opacity: 0.6;
    }
    
    input {
      width: 100%;
      padding: 0.75rem 1rem 0.75rem 3rem;
      background: rgba(0, 0, 0, 0.5);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      font-size: 0.9rem;
      
      &:focus {
        outline: none;
        border-color: ${props => props.theme?.colors?.accent || '#800000'};
      }
      
      &::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }
    }
  }
`;

const CartItem = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative; // ✅ For absolute positioning of remove button
  
  @media (max-width: 768px) {
    padding: 0.65rem;
    gap: 0.65rem;
    flex-wrap: wrap; // ✅ Allow wrapping for quantity controls
  }
  
  img {
    width: 60px;
    height: 60px;
    border-radius: 6px;
    object-fit: cover;
    flex-shrink: 0;
    
    @media (max-width: 768px) {
      width: 50px;
      height: 50px;
    }
  }
  
  .item-details {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center; // ✅ Center content vertically
    
    @media (max-width: 768px) {
      // ✅ CHANGED: Keep next to image, don't take full width
      flex: 1;
      min-width: 0;
      padding-right: 2rem; // ✅ Space for remove button
    }
    
    .item-name {
      font-weight: 600;
      margin-bottom: 0.25rem;
      color: ${props => props.theme?.colors?.text || 'white'};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.9rem;
      
      @media (max-width: 768px) {
        font-size: 0.85rem;
        line-height: 1.2;
        margin-bottom: 0.15rem;
        // ✅ Allow 2 lines on mobile
        white-space: normal;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
    }
    
    .item-price {
      color: ${props => props.theme?.colors?.accent || '#800000'};
      font-size: 0.85rem;
      font-weight: 700;
      
      @media (max-width: 768px) {
        font-size: 0.8rem;
      }
    }
  }
  
  .quantity-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0; // ✅ Prevent controls from shrinking
    
    @media (max-width: 768px) {
      // ✅ CHANGED: Take full width below, but with top border
      width: 100%;
      flex-basis: 100%; // ✅ Force to new row
      justify-content: center;
      margin-top: 0.5rem;
      padding-top: 0.5rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    button {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
      background: transparent;
      color: ${props => props.theme?.colors?.accent || '#800000'};
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;
      flex-shrink: 0;
      
      @media (max-width: 768px) {
        width: 32px;
        height: 32px;
      }
      
      &:hover:not(:disabled) {
        background: ${props => props.theme?.colors?.accent || '#800000'};
        color: white;
      }
      
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      
      svg {
        width: 14px;
        height: 14px;
        
        @media (max-width: 768px) {
          width: 16px;
          height: 16px;
        }
      }
    }
    
    .quantity {
      min-width: 30px;
      text-align: center;
      font-weight: 700;
      color: ${props => props.theme?.colors?.text || 'white'};
      font-size: 0.95rem;
      
      @media (max-width: 768px) {
        min-width: 40px;
        font-size: 1.05rem;
      }
    }
  }
  
  .remove-btn {
    background: transparent;
    border: none;
    color: #e74c3c;
    cursor: pointer;
    padding: 0.25rem;
    flex-shrink: 0;
    align-self: flex-start; // ✅ Align to top on desktop
    
    @media (max-width: 768px) {
      position: absolute;
      top: 0.65rem;
      right: 0.65rem;
      padding: 0.35rem;
    }
    
    &:hover {
      transform: scale(1.1);
    }
    
    svg {
      width: 16px;
      height: 16px;
      
      @media (max-width: 768px) {
        width: 18px;
        height: 18px;
      }
    }
  }
`;

const StatusMessage = styled.div`
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  
  &.error {
    background: rgba(244, 67, 54, 0.1);
    border: 1px solid rgba(244, 67, 54, 0.3);
    color: #F44336;
  }
  
  &.success {
    background: rgba(76, 175, 80, 0.1);
    border: 1px solid rgba(76, 175, 80, 0.3);
    color: #4CAF50;
  }
  
  &.info {
    background: rgba(33, 150, 243, 0.1);
    border: 1px solid rgba(33, 150, 243, 0.3);
    color: #2196F3;
  }
`;



const CartDropdown = ({ 
  isOpen, 
  onClose, 
  theme, 
  cartItems, 
  onUpdateCart,
  onSendRequest,
  isPublicShop = false
}) => {
  const [loading, setLoading] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const navigate = useNavigate();
  const [showGuestLookup, setShowGuestLookup] = useState(false);
  const [lookupPhone, setLookupPhone] = useState('');
  const [lookupStatus, setLookupStatus] = useState({ type: '', message: '' });
  const [lookupLoading, setLookupLoading] = useState(false);
  const isAuthenticated = !!auth.currentUser && !auth.currentUser.isAnonymous; // ✅ UPDATED
  const { isOwnShop, activeShopId, setActiveShop } = useActiveShop();
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [error, setError] = useState('');
  const [hasValidGuestSession, setHasValidGuestSession] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const isAppleDevice = typeof window !== 'undefined' && /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent) && 'ApplePaySession' in window;

  const updateQuantity = (itemId, delta) => {
    const updatedCart = cartItems.map(item => {
      if (item.id === itemId) {
        const newQty = Math.max(1, item.cartQuantity + delta);
        return { ...item, cartQuantity: newQty };
      }
      return item;
    });
    onUpdateCart(updatedCart);
  };

  useEffect(() => {
  if (!isAuthenticated) {
    const existingSession = localStorage.getItem('guest_session');
    
    if (existingSession) {
      try {
        const session = JSON.parse(existingSession);
        const expiresAt = new Date(session.expiresAt);
        
        if (expiresAt > new Date()) {
          // Valid session exists
          setShowGuestForm(false);
          setGuestName(session.displayName);
          setGuestPhone(session.phoneNumber);
          setHasValidGuestSession(true); // ✅ NEW
          
          console.log('✅ Valid guest session found - form hidden');
          console.log('👤 Continuing as:', session.displayName);
        } else {
          setHasValidGuestSession(false); // ✅ NEW
        }
      } catch (error) {
        console.error('Error checking guest session:', error);
        setHasValidGuestSession(false); // ✅ NEW
      }
    } else {
      setHasValidGuestSession(false); // ✅ NEW
    }
  } else {
    setHasValidGuestSession(false); // Authenticated users don't need this
  }
}, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && cartItems.length > 0) {
      const currentUserId = auth.currentUser?.uid;

      const filteredCart = cartItems.filter(item => item.shopId !== currentUserId);

      if (filteredCart.length !== cartItems.length) {
        const removedCount = cartItems.length - filteredCart.length;
        console.log(`🗑️ Removed ${removedCount} item(s) from your own shop`);
        onUpdateCart(filteredCart);

        if (removedCount > 0) {
          setError(`Removed ${removedCount} item(s) from your own shop from cart`);
          setTimeout(() => setError(''), 3000);
        }
      }
    }
  }, [isAuthenticated, auth.currentUser?.uid]);


  const handleLoginRedirect = async () => {
  try {
    setLoading(true);
    
    // ✅ ADD: Set flag for auto-send after login
    sessionStorage.setItem('just_logged_in_for_cart', 'true');
    
    console.log('🔐 Attempting Google Sign-In from cart...');
    
    const result = await safeSignInWithPopup(googleProvider);
      const user = result.user;
      const emailName = user.email.split('@')[0];
      const displayName = user.displayName || emailName;

      console.log('✅ Google Sign-In successful:', user.uid);
      
      const { checkExistingShop, saveShopData, generateUsername } = await import('../firebase/firebaseService');
      const hasShop = await checkExistingShop(user.uid);
      
      if (!hasShop) {
        console.log('📝 No account found - creating new shop with default settings...');
        
        const marketplaceNames = [
          "Side Hustle", "Sailor", "Plug", "Stash", "Vault", "Block", "Corner", 
          "Supply", "Cashout", "Street", "Motion", "Deal", "Flip", "Drop", 
          "Link", "Run", "Load", "Bag", "Stack", "Exchange"
        ];
        
        const randomMarketplaceName = marketplaceNames[Math.floor(Math.random() * marketplaceNames.length)];
        const shopName = `${randomMarketplaceName} ${Math.floor(Math.random() * 999)}`;
        const username = await generateUsername(shopName);
        
        const { WELCOME_STYLES } = await import('../theme/welcomeStyles');
        
        const defaultShopData = {
          name: displayName,
          username: username,
          description: ``,
          profile: null,
          theme: WELCOME_STYLES.STYLE_1,
          isRestaurant: false,
          selectedHomeTemplate: 1,
          items: [],
          services: [],
          layout: {
            namePosition: 'left',
            tabPosition: 'top',
            nameSize: 2.5
          },
          profileBackgroundImages: [
            'https://images.unsplash.com/photo-1522124624696-7ea32eb9592c?q=80&w=1169&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
            'https://images.unsplash.com/photo-1444080748397-f442aa95c3e5?q=80&w=1332&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
            'https://images.unsplash.com/photo-1520034475321-cbe63696469a?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
          ],
          homeSections: [
            {
              id: 'hero-1',
              type: 'hero-banner',
              order: 0,
              config: {
                headline: displayName,
                subtitle: `Welcome to ${displayName}`,
                backgroundImage: null,
                height: '70vh'
              }
            },
            {
              id: 'mission-1',
              type: 'mission-statement',
              order: 1,
              config: {
                title: 'Mission',
                content: 'We have top quality products ready to be delivered with exceptional customer service. Our commitment to quality drives everything we do.'
              }
            }
          ],
          createdAt: new Date().toISOString()
        };
        
        await saveShopData(user.uid, defaultShopData);
        sessionStorage.setItem('is_new_account', 'true');
        console.log('✅ Default shop created successfully');
      }
      
      setLoading(false);
      
    } catch (error) {
      console.error('❌ Google Sign-In error:', error);
      
      if (error.code === 'auth/popup-blocked') {
        alert('Popup was blocked. Please allow popups and try again.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        console.log('User closed the popup');
      } else {
        alert('Failed to sign in with Google. Please try again.');
      }
      
      setLoading(false);
    }
  };

  const removeItem = (itemId) => {
    const updatedCart = cartItems.filter(item => item.id !== itemId);
    onUpdateCart(updatedCart);
  };

  const calculateTotal = () => {
    const subtotal = cartItems.reduce((sum, item) => 
      sum + (parseFloat(item.price) * item.cartQuantity), 0
    );
    const total = subtotal; // Customer pays only item prices

    return { subtotal, transactionFee: 0, salesTax: 0, total };
  };

  const hasOnlyOwnItems = () => {
  if (!isAuthenticated || !auth.currentUser) return false;
  const currentUserId = auth.currentUser.uid;
  return cartItems.every(item => item.shopId === currentUserId);
};

const hasValidItems = () => {
  if (!isAuthenticated || !auth.currentUser) return cartItems.length > 0;
  const currentUserId = auth.currentUser.uid;
  return cartItems.some(item => item.shopId !== currentUserId);
};

  // ✅ COMPLETELY REWRITTEN handleSend
  const handleSend = useCallback(async () => {
  try {
    setLoading(true);

    if (isAuthenticated && auth.currentUser) {
      const currentUserId = auth.currentUser.uid;

      // If a shared store is active, switch back to own account before ordering
      if (!isOwnShop && activeShopId && activeShopId !== currentUserId) {
        console.log('🔄 Shared store active — switching to main account before order');
        setActiveShop(currentUserId);
      }
    }

    let guestAccount = null;
    
    if (!isAuthenticated) {
      // ✅ FIX: Check ONLY guest_session
      const existingSession = localStorage.getItem('guest_session');
      
      if (existingSession) {
        try {
          const session = JSON.parse(existingSession);
          const expiresAt = new Date(session.expiresAt);
          
          if (expiresAt > new Date()) {
            console.log('✅ Using existing guest session:', session.guestId);
            
            // Sign in with existing guest UID
            await signInAnonymously(auth);
            
            guestAccount = {
              uid: session.guestId,  // ✅ Firebase UID
              displayName: session.displayName,
              phoneNumber: session.phoneNumber
            };
            
            console.log('✅ Reusing guest account - no need to checkout again');
          } else {
            console.log('⚠️ Guest session expired, will create new one');
            localStorage.removeItem('guest_session');
          }
        } catch (parseError) {
          console.error('Error parsing guest session:', parseError);
          localStorage.removeItem('guest_session');
        }
      }
      
      // Only prompt for guest info if NO valid session exists
      if (!guestAccount) {
        // Validate guest input
        if (!guestName.trim() || !guestPhone.trim()) {
          alert('Please enter your name and phone number');
          setLoading(false);
          return;
        }

        const cleanedPhone = guestPhone.replace(/\D/g, '');
        if (cleanedPhone.length !== 10) {
          alert('Please enter a valid 10-digit phone number');
          setLoading(false);
          return;
        }

        // Create NEW Firebase anonymous account
        console.log('🎭 Creating NEW guest Firebase account...');
        try {
          guestAccount = await GuestAuthService.createGuestAccount({
            firstName: guestName.trim(),
            phoneNumber: cleanedPhone,
            displayName: guestName.trim()
          });

          console.log('✅ New guest account created:', {
            uid: guestAccount.uid,
            displayName: guestAccount.displayName
          });

        } catch (authError) {
          console.error('❌ Failed to create guest account:', authError);
          alert('Failed to create guest account. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Mark session as active so the form stays hidden for subsequent orders
      if (guestAccount) {
        setHasValidGuestSession(true);
        if (guestAccount.displayName && !guestName) setGuestName(guestAccount.displayName);
      }
    }

    // Group items by seller — skip only items from shared shops the user manages as staff/admin
    const itemsBySeller = {};
    cartItems.forEach(item => {
      if (isAuthenticated && !isOwnShop && activeShopId && item.shopId === activeShopId) {
        console.warn('⚠️ Skipping item from managed shared shop:', item.name);
        return;
      }
      if (!itemsBySeller[item.shopId]) {
        itemsBySeller[item.shopId] = [];
      }
      itemsBySeller[item.shopId].push(item);
    });

    if (Object.keys(itemsBySeller).length === 0) {
      alert('Your cart is empty.');
      setLoading(false);
      return;
    }

    // Create orders for each seller
    const chatIds = [];
    
    for (const [sellerId, items] of Object.entries(itemsBySeller)) {
      // Add items to cart one by one
      for (const item of items) {
        const result = await TransactionService.addToCart(
          item.id,
          sellerId,
          parseFloat(item.price),
          item.cartQuantity,
          'inperson',
          false,
          item.customization || null
        );

        if (result.transactionId && !chatIds.includes(result.transactionId)) {
          chatIds.push(result.transactionId);
        }
      }

      // Send the cart
      if (chatIds.length > 0) {
        const latestChatId = chatIds[chatIds.length - 1];
        
        if (isAuthenticated) {
          // Authenticated user flow
          await TransactionService.sendCart(latestChatId, 'authenticated', null, paymentMethod);
        } else {
          // Guest flow using Firebase UID
          const cleanedPhone = guestPhone.replace(/\D/g, '');

          const guestInfo = {
            guestId: guestAccount.uid,  // ✅ Firebase UID
            firstName: guestName.trim(),
            phoneNumber: cleanedPhone,
            displayName: guestName.trim()
          };

          await TransactionService.sendCart(latestChatId, 'guest', guestInfo, paymentMethod);
        }
      }
    }

    // Clear cart and redirect
    onUpdateCart([]);
    onClose();
    
    if (chatIds.length > 0) {
      navigate(`/messages?chat=${chatIds[0]}`);
    }

  } catch (error) {
    console.error('❌ Error sending cart:', error);
    alert('Error sending cart: ' + error.message);
  } finally {
    setLoading(false);
  }
}, [isAuthenticated, cartItems, guestName, guestPhone, navigate, onUpdateCart, onClose, paymentMethod]);
// ✅ Add all dependencies that handleSend uses

useEffect(() => {
  // Auto-send cart after login if cart has items
  if (isAuthenticated && cartItems.length > 0 && !loading) {
    const justLoggedIn = sessionStorage.getItem('just_logged_in_for_cart');
    
    if (justLoggedIn) {
      sessionStorage.removeItem('just_logged_in_for_cart');
      console.log('🔄 Auto-sending cart after login');
      
      setTimeout(() => {
        handleSend();
      }, 500);
    }
  }
}, [isAuthenticated, cartItems.length, loading, handleSend]);

  const handlePublicShopSend = () => {
    setLoading(true);
    onSendRequest();
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e, setter) => {
    const formatted = formatPhoneNumber(e.target.value);
    setter(formatted);
  };

  const handleGuestOrderLookup = async () => {
  const cleanedPhone = lookupPhone.replace(/\D/g, '');
  
  if (cleanedPhone.length !== 10) {
    setLookupStatus({ type: 'error', message: 'Please enter a valid 10-digit phone number' });
    return;
  }
  
  setLookupLoading(true);
  setLookupStatus({ type: 'info', message: 'Searching for your orders...' });
  
  try {
    console.log('🔍 Looking up guest account by phone:', cleanedPhone);
    
    // ✅ FIXED: Use GuestAuthService.findGuestByPhone
    const guestAccount = await GuestAuthService.findGuestByPhone(cleanedPhone);
    
    if (!guestAccount) {
      setLookupStatus({ 
        type: 'error', 
        message: 'No active orders found with this phone number' 
      });
      setLookupLoading(false);
      return;
    }
    
    console.log('✅ Found guest account:', guestAccount.uid);
    
    // ✅ FIXED: Sign in anonymously
    const { signInAnonymously } = await import('firebase/auth');
    await signInAnonymously(auth);
    
    // ✅ Store session with Firebase UID
    localStorage.setItem('guest_session', JSON.stringify({
      guestId: guestAccount.uid,  // ✅ Firebase UID
      phoneNumber: cleanedPhone,
      displayName: guestAccount.displayName || 'Guest',
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    }));
    
    setLookupStatus({ 
      type: 'success', 
      message: 'Account found! Redirecting to your orders...' 
    });
    
    setTimeout(() => {
      onClose();
      navigate('/messages');
    }, 1500);
    
  } catch (error) {
    console.error('❌ Error searching for guest orders:', error);
    setLookupStatus({ 
      type: 'error', 
      message: 'Failed to search for orders. Please try again.' 
    });
  } finally {
    setLookupLoading(false);
  }
};

  const totals = calculateTotal();

  return (
    <DropdownContainer isOpen={isOpen} theme={theme}>
      <CartHeader theme={theme}>
        <h3>
          <ShoppingCart size={20} />
          Shopping Cart ({cartItems.length})
        </h3>
        <button className="close-btn" onClick={onClose}>
          <X size={20} />
        </button>
      </CartHeader>

      <CartContent>
  {/* ✅ NEW: Show guest session indicator */}
  {!isAuthenticated && guestName && (
    <div style={{
      background: 'rgba(76, 175, 80, 0.1)',
      border: '1px solid rgba(76, 175, 80, 0.3)',
      borderRadius: '8px',
      padding: '0.75rem',
      marginBottom: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.85rem'
    }}>
      <User size={14} style={{ color: '#4CAF50' }} />
      <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
        Ordering as <strong style={{ color: '#4CAF50' }}>{guestName}</strong>
      </span>
    </div>
  )}
  
        {cartItems.length === 0 ? (
          <>
            <EmptyCart>
              <ShoppingCart />
              <h4>Your cart is empty</h4>
              <p>Add items to get started</p>
            </EmptyCart>
            
            {/* Only show guest lookup on welcome page, not public shop */}
            {!isPublicShop && !isAuthenticated && (
              <>
                <GuestLookupButton 
                  onClick={() => setShowGuestLookup(!showGuestLookup)}
                  theme={theme}
                >
                  <Search size={16} />
                  {showGuestLookup ? 'Hide Guest Orders' : 'Access Guest Orders'}
                </GuestLookupButton>
                
                {showGuestLookup && (
  <GuestLookupSection theme={theme}>
    <div className="lookup-header">
      <h4>Find Your Orders</h4>
      <button onClick={() => {
        setShowGuestLookup(false);
        setLookupPhone('');
        setLookupStatus({ type: '', message: '' });
      }}>
        <X size={16} />
      </button>
    </div>
    
    {lookupStatus.message && (
      <StatusMessage className={lookupStatus.type}>
        {lookupStatus.message}
      </StatusMessage>
    )}
    
    <div className="input-group">
      <Phone size={16} />
      <input
        type="tel"
        placeholder="Phone Number"
        value={lookupPhone}
        onChange={(e) => handlePhoneChange(e, setLookupPhone)}
        maxLength={14}
        disabled={lookupLoading}
      />
    </div>
    
    <SendButton
      onClick={handleGuestOrderLookup}
      disabled={lookupLoading || lookupPhone.replace(/\D/g, '').length !== 10}
      theme={theme}
    >
      <Search size={18} />
      {lookupLoading ? 'Searching...' : 'Find My Orders'}
    </SendButton>
  </GuestLookupSection>
)}
              </>
            )}
          </>
        ) : (
          cartItems.map(item => (
            <CartItem key={`${item.shopId}-${item.id}`} theme={theme}>
              {item.images?.[0] && (
                <img src={item.images[0]} alt={item.name} />
              )}
              <div className="item-details">
                <div className="item-name">{item.name}</div>
                {item.customization?.options && Object.keys(item.customization.options).length > 0 && (
                  <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.1rem' }}>
                    {Object.entries(item.customization.options).map(([opt, qty]) => (
                      <span key={opt} style={{ display: 'block' }}>{opt}: ×{qty}</span>
                    ))}
                  </div>
                )}
                <div className="item-price">
                  ${(parseFloat(item.price) * item.cartQuantity).toFixed(2)}
                </div>
              </div>
              {item.customization?.options && Object.keys(item.customization.options).length > 0 ? (
                <div className="quantity-controls">
                  <div className="quantity" style={{ minWidth: '2rem', textAlign: 'center' }}>×{item.cartQuantity}</div>
                </div>
              ) : (
                <div className="quantity-controls">
                  <button
                    onClick={() => updateQuantity(item.id, -1)}
                    disabled={item.cartQuantity <= 1}
                  >
                    <Minus />
                  </button>
                  <div className="quantity">{item.cartQuantity}</div>
                  <button
                    onClick={() => updateQuantity(item.id, 1)}
                    disabled={item.cartQuantity >= parseInt(item.quantity)}
                  >
                    <Plus />
                  </button>
                </div>
              )}
              <button
                className="remove-btn"
                onClick={() => removeItem(item.id)}
              >
                <Trash2 size={16} />
              </button>
            </CartItem>
          ))
        )}
      </CartContent>

      {cartItems.length > 0 && (
  <>
    {isPublicShop ? (
      <CartSummary theme={theme}>
        <div className="summary-row total">
          <span>Total:</span>
          <span>${totals.total.toFixed(2)}</span>
        </div>

        {/* Payment method selection */}
        <div style={{ marginBottom: '0.75rem', padding: '0.6rem 0.75rem', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', border: `1px solid ${theme?.colors?.accent}25` }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.5rem', color: theme?.colors?.accent, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Payment Method
          </div>
          {[
            { value: 'card',           label: '💳  Credit / Debit Card' },
            { value: 'google_pay',     label: 'G  Google Pay' },
            ...(isAppleDevice ? [{ value: 'apple_pay', label: '🍎  Apple Pay' }] : []),
            { value: 'tap_card',       label: '📲  Tap to Pay (NFC)' },
            { value: 'cash_on_pickup', label: '💵  Cash on Pickup' },
            { value: 'card_on_pickup', label: '🫳  Card on Pickup (seller charges)' },
          ].map(opt => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.25rem 0', fontSize: '0.8rem', color: theme?.colors?.text || '#fff' }}>
              <input
                type="radio"
                name="paymentMethod"
                value={opt.value}
                checked={paymentMethod === opt.value}
                onChange={() => setPaymentMethod(opt.value)}
                style={{ accentColor: theme?.colors?.accent }}
              />
              {opt.label}
            </label>
          ))}
        </div>

        <SendButton
          onClick={isAuthenticated ? handlePublicShopSend : handleLoginRedirect}
          disabled={loading}
          theme={theme}
        >
          {loading ? (
            <>
              <LoadingSpinner size={18} />
              <span>{isAuthenticated ? 'Sending...' : 'Signing in...'}</span>
            </>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {isAuthenticated
                ? (paymentMethod === 'cash_on_pickup' ? 'Request Order (Pay Cash at Pickup)'
                  : paymentMethod === 'card_on_pickup' ? 'Request Order (Pay Card at Pickup)'
                  : 'Request Order')
                : 'Order with Google'}
            </>
          )}
        </SendButton>
      </CartSummary>
    ) : (
      <CartSummary theme={theme}>
        <div className="summary-row total">
          <span>Total:</span>
          <span>${totals.total.toFixed(2)}</span>
        </div>
        
        {/* Payment method selection */}
        <div style={{ marginBottom: '0.75rem', padding: '0.6rem 0.75rem', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', border: `1px solid ${theme?.colors?.accent}25` }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.5rem', color: theme?.colors?.accent, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Payment Method
          </div>
          {[
            { value: 'card',           label: '💳  Credit / Debit Card' },
            { value: 'google_pay',     label: 'G  Google Pay' },
            ...(isAppleDevice ? [{ value: 'apple_pay', label: '🍎  Apple Pay' }] : []),
            { value: 'tap_card',       label: '📲  Tap to Pay (NFC)' },
            { value: 'cash_on_pickup', label: '💵  Cash on Pickup' },
            { value: 'card_on_pickup', label: '🫳  Card on Pickup (seller charges)' },
          ].map(opt => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.25rem 0', fontSize: '0.8rem', color: theme?.colors?.text || '#fff' }}>
              <input
                type="radio"
                name="paymentMethod"
                value={opt.value}
                checked={paymentMethod === opt.value}
                onChange={() => setPaymentMethod(opt.value)}
                style={{ accentColor: theme?.colors?.accent }}
              />
              {opt.label}
            </label>
          ))}
        </div>

        {/* Request Order / Sign in with Google — single unified button */}
        <SendButton
          onClick={isAuthenticated ? handleSend : handleLoginRedirect}
          disabled={loading}
          theme={theme}
        >
          {loading ? (
            <>
              <LoadingSpinner size={18} />
              <span>{isAuthenticated ? 'Sending...' : 'Signing in...'}</span>
            </>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {isAuthenticated
                ? (paymentMethod === 'cash_on_pickup' ? 'Request Order (Pay Cash at Pickup)'
                  : paymentMethod === 'card_on_pickup' ? 'Request Order (Pay Card at Pickup)'
                  : 'Request Order')
                : 'Order with Google'}
            </>
          )}
        </SendButton>
      </CartSummary>
    )}
  </>
)}
    </DropdownContainer>
  );
};

export default CartDropdown;